import React, { useState } from 'react';
import PatientControlButton from 'components/atoms/patentControlButton';
import ControlButtonDropdown from 'components/atoms/controlPanelDropdownButton/controlButtonDropdown';
import { useTranslation } from 'react-i18next';
import {
  useDoctorSendSms,
  useSendSms,
  useSmsButtons,
} from 'infrastructure/hooks/control-panel';
import { isRpmModule as isRpmModuleFunction } from 'infrastructure/functions';
import useUser from 'utils/useUser';
import SmsControlIcon from 'components/atoms/icons/smsControlIcon';
import SmsModal from 'components/templates/rpm/patients/patient/messages/modal';
import SmsDoctorModal from 'components/organisms/controlPanel/smsDoctorModal';
import BaseGrid from 'components/atoms/base-grid';
import { useAccess } from 'infrastructure/hooks/access/useAccess';
import { useSendScheduledMessage } from 'infrastructure/hooks/smsDashboard/useSendScheduledMessage';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { useAvailabilityAlert } from 'components/molecules/smsDashboard/availabilityAlert/use-availability-alert';
import { PlatformEnums, type ModuleType } from 'infrastructure/enums';

import type { ISendSmsDataProps } from 'infrastructure/hooks/control-panel/api';

const ToggleButton: React.FC<{ label: string; disabled?: boolean }> = (
  props,
) => {
  const { label, disabled } = props;
  return (
    <PatientControlButton
      label={label}
      Icon={SmsControlIcon}
      disabled={disabled}
    />
  );
};

interface IProps {
  module: ModuleType;
  patientGuid: string;
}

const SmsButtonsController: React.FC<IProps> = (props) => {
  const { module, patientGuid } = props;
  const { t } = useTranslation();

  const [currentModule, setCurrentModule] = useState<ModuleType | null>(null);
  const [smsPopupOpen, setSmsPopupOpen] = useState(false);
  const [doctorSmsPopupOpen, setDoctorSmsPopupOpen] = useState(false);

  const { userType, isMobileDevice } = useUser();
  const {
    doctorInfo,
    isBlockCcmModule,
    isBlockRpmModule,
    isCcmActionDisabled,
    isRpmModule,
    isCcmModule,
    isCombinedModule,
    patientInfo,
    isReadOnlyRole,
  } = useAccess({
    module,
    patientGuid,
  });

  const smsButtons = useSmsButtons({
    isAdminPanel: false,
    setSmsPopupOpen,
    userType,
    doctorWorkNumber: doctorInfo?.workNumber ?? undefined,
    setDoctorSmsPopupOpen,
    setCurrentModule,
  });

  const { checkAvailability, isLoading, Alert } = useAvailabilityAlert({
    patientGuid,
    withAlert: true,
  });
  const { sendSms, smsLoading } = useSendSms();
  const { sendScheduledMessage, sendScheduledMessageLoading } =
    useSendScheduledMessage(
      {},
      {
        onSuccess() {
          notificationController.info({
            message: t(
              'smsDashboard.message_scheduled_for_closest_available_time',
            ),
          });
        },
      },
    );
  const { sendDoctorSms, doctorSmsLoading } = useDoctorSendSms();

  const loading = isLoading || smsLoading || sendScheduledMessageLoading;

  const handleSubmit = async (data: ISendSmsDataProps) => {
    const patientPhone = data.isPrimaryNumber
      ? patientInfo?.loginInfo?.phone
      : patientInfo?.homeNumber;
    const { sendNow, sendLater, nextAvailableTime } = await checkAvailability(
      patientPhone!,
    );

    if (sendNow) {
      const res = await sendSms(data);
      return res;
    }

    if (sendLater && patientPhone && nextAvailableTime) {
      const res = await sendScheduledMessage({
        message: data.message,
        from: data.isRpmPanel ? PlatformEnums.RPM : PlatformEnums.CCM,
        patientPhone,
        scheduledTime: nextAvailableTime,
        patientGuid,
      });
      return res;
    }

    if (!sendNow && !sendLater) return { error: 'close alert' };

    return { error: null };
  };

  const getLabel = (prefix: string) => {
    const label = t('labels.sms');
    if (isCombinedModule) return `${prefix} ${label}`;
    return label;
  };

  const columns = isMobileDevice || !isCombinedModule ? 1 : 2;

  return (
    <BaseGrid columns={columns}>
      {(isRpmModule || isCombinedModule) && (
        <ControlButtonDropdown
          toggleBtn={
            <ToggleButton
              label={getLabel(t('labels.rpm'))}
              disabled={
                isReadOnlyRole || isCcmActionDisabled || isBlockRpmModule
              }
            />
          }
          options={smsButtons}
          isRpm
        />
      )}

      {(isCcmModule || isCombinedModule) && (
        <ControlButtonDropdown
          toggleBtn={
            <ToggleButton
              label={getLabel(t('labels.ccm'))}
              disabled={
                isReadOnlyRole || isCcmActionDisabled || isBlockCcmModule
              }
            />
          }
          options={smsButtons}
          isRpm={false}
        />
      )}
      {smsPopupOpen && (
        <SmsModal
          isOpen={smsPopupOpen}
          toggle={() => {
            setSmsPopupOpen(false);
            setCurrentModule(null);
          }}
          patientGuid={patientGuid}
          loading={loading}
          submit={handleSubmit}
          isRpmPanel={
            (currentModule && isRpmModuleFunction(currentModule)) || isRpmModule
          }
        />
      )}
      <SmsDoctorModal
        isOpen={doctorSmsPopupOpen}
        toggle={setDoctorSmsPopupOpen}
        patientGuid={patientGuid}
        loading={doctorSmsLoading}
        submit={sendDoctorSms}
      />
      {Alert}
    </BaseGrid>
  );
};

export default SmsButtonsController;
